import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import DraggableMediaList from '../components/DraggableMediaList';
import './AdminPropertyForm.css';

function AdminPropertyForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [hosts, setHosts] = useState([]);
  const [searchHost, setSearchHost] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);
  const [uploadingVideos, setUploadingVideos] = useState(false);
  const [formData, setFormData] = useState({
    host_id: '',
    title: '',
    description: '',
    property_type: 'apartment',
    property_category: 'single_unit',
    listing_subtype: 'entire_place',
    square_footage: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    bedrooms: 1,
    bathrooms: 1,
    beds: 1,
    max_guests: 2,
    base_guests: 2,
    price_per_night: '',
    extra_guest_price: 0,
    cleaning_fee: 0,
    service_fee: 0,
    minimum_nights: 1,
    maximum_nights: 365,
    check_in_time: '15:00',
    check_out_time: '11:00',
    cancellation_policy: 'flexible',
    is_active: true,
    is_featured: false
  });

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/');
      return;
    }

    if (isEdit) {
      fetchProperty();
    }
    fetchHosts();
  }, [user, navigate, isEdit, id]);

  const fetchProperty = async () => {
    try {
      const response = await api.get(`/api/admin/properties/${id}`);
      const property = response.data.property;
      setFormData({
        host_id: property.host_id,
        title: property.title,
        description: property.description,
        property_type: property.property_type,
        property_category: property.property_category || 'single_unit',
        listing_subtype: property.listing_subtype || 'entire_place',
        square_footage: property.square_footage || '',
        address: property.address,
        city: property.city,
        state: property.state,
        country: property.country,
        zip_code: property.zip_code || '',
        bedrooms: property.bedrooms,
        bathrooms: property.bathrooms,
        beds: property.beds,
        max_guests: property.max_guests,
        base_guests: property.base_guests || 2,
        price_per_night: property.price_per_night,
        extra_guest_price: property.extra_guest_price || 0,
        cleaning_fee: property.cleaning_fee,
        service_fee: property.service_fee,
        minimum_nights: property.minimum_nights,
        maximum_nights: property.maximum_nights,
        check_in_time: property.check_in_time,
        check_out_time: property.check_out_time,
        cancellation_policy: property.cancellation_policy,
        is_active: property.is_active,
        is_featured: property.is_featured
      });
      setExistingImages(property.images || []);
      setExistingVideos(property.videos || []);
    } catch (err) {
      toast.error(t('admin.propertyForm.failedToLoad'));
      navigate('/admin/properties');
    }
  };

  const fetchHosts = async () => {
    try {
      const response = await api.get('/api/admin/users', {
        params: { role: 'host', per_page: 100 }
      });
      setHosts(response.data.users);
    } catch (err) {
      toast.error(t('admin.propertyForm.failedToLoadHosts'));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(prev => [...prev, ...files]);
  };

  const handleRemoveImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = async (index) => {
    if (!window.confirm(t('admin.propertyForm.deleteImageConfirm'))) {
      return;
    }

    try {
      const imageToRemove = existingImages[index];
      await api.delete(`/api/properties/${id}/images/${imageToRemove.id}`);
      setExistingImages(prev => prev.filter((_, i) => i !== index));
      toast.success(t('admin.propertyForm.imageDeleted'));
    } catch (err) {
      toast.error(t('admin.propertyForm.failedToDeleteImage'));
    }
  };

  const handleReorderImages = async (reorderedImages) => {
    try {
      // Update local state immediately for better UX
      setExistingImages(reorderedImages);

      // Send the new order to the backend
      const imageIds = reorderedImages.map(img => img.id);
      await api.put(`/api/properties/${id}/images/reorder`, {
        image_ids: imageIds
      });

      toast.success(t('admin.propertyForm.imageOrderUpdated'));
    } catch (error) {
      console.error('Error reordering images:', error);
      toast.error(t('admin.propertyForm.failedToReorderImages'));
      // Reload property data on error
      if (isEdit) {
        fetchProperty();
      }
    }
  };

  const handleSetPrimaryImage = async (index) => {
    const reorderedImages = [...existingImages];
    const [primaryImage] = reorderedImages.splice(index, 1);
    reorderedImages.unshift(primaryImage);
    await handleReorderImages(reorderedImages);
  };

  const uploadImages = async (propertyId) => {
    if (selectedImages.length === 0) return;

    setUploadingImages(true);
    try {
      for (let i = 0; i < selectedImages.length; i++) {
        const file = selectedImages[i];
        const formData = new FormData();
        formData.append('file', file);

        // Upload the image file
        const uploadResponse = await api.post('/api/uploads/image?folder=properties', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const imageUrl = uploadResponse.data.image_url;

        // Add the image to the property
        await api.post(`/api/properties/${propertyId}/images`, {
          image_url: imageUrl,
          is_primary: existingImages.length === 0 && i === 0 // First image is primary if no existing images
        });
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error(t('admin.propertyForm.failedToUploadImages'));
    } finally {
      setUploadingImages(false);
    }
  };

  const handleVideoSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedVideos(prev => [...prev, ...files]);
  };

  const handleRemoveVideo = (index) => {
    setSelectedVideos(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingVideo = async (index) => {
    if (!window.confirm(t('admin.propertyForm.deleteVideoConfirm'))) {
      return;
    }

    try {
      const videoToRemove = existingVideos[index];
      await api.delete('/api/uploads/video', {
        data: { video_url: videoToRemove.video_url }
      });
      setExistingVideos(prev => prev.filter((_, i) => i !== index));
      toast.success(t('admin.propertyForm.videoDeleted'));
    } catch (err) {
      toast.error(t('admin.propertyForm.failedToDeleteVideo'));
    }
  };

  const handleReorderVideos = async (reorderedVideos) => {
    try {
      setExistingVideos(reorderedVideos);
      const videoIds = reorderedVideos.map(vid => vid.id);
      await api.put(`/api/properties/${id}/videos/reorder`, {
        video_ids: videoIds
      });
      toast.success(t('admin.propertyForm.videoOrderUpdated'));
    } catch (error) {
      console.error('Error reordering videos:', error);
      toast.error(t('admin.propertyForm.failedToReorderVideos'));
      if (isEdit) {
        fetchProperty();
      }
    }
  };

  const uploadVideos = async (propertyId) => {
    if (selectedVideos.length === 0) return;

    setUploadingVideos(true);
    try {
      for (let i = 0; i < selectedVideos.length; i++) {
        const file = selectedVideos[i];
        const formData = new FormData();
        formData.append('file', file);

        const uploadResponse = await api.post('/api/uploads/video?folder=properties', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const videoUrl = uploadResponse.data.video_url;

        await api.post(`/api/properties/${propertyId}/videos`, {
          video_url: videoUrl
        });
      }
    } catch (error) {
      console.error('Error uploading videos:', error);
      toast.error(t('admin.propertyForm.failedToUploadVideos'));
    } finally {
      setUploadingVideos(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let propertyId = id;

      if (isEdit) {
        await api.put(`/api/admin/properties/${id}`, formData);
        toast.success(t('admin.propertyForm.updatedSuccess'));
      } else {
        const response = await api.post('/api/admin/properties', formData);
        propertyId = response.data.property.id;
        toast.success(t('admin.propertyForm.createdSuccess'));
      }

      // Upload images if any were selected
      if (selectedImages.length > 0) {
        await uploadImages(propertyId);
      }

      // Upload videos if any were selected
      if (selectedVideos.length > 0) {
        await uploadVideos(propertyId);
      }

      navigate('/admin/properties');
    } catch (err) {
      toast.error(err.response?.data?.error || t('admin.propertyForm.failedToSave'));
    } finally {
      setLoading(false);
    }
  };

  const filteredHosts = hosts && Array.isArray(hosts) ? hosts.filter(host =>
    host.email.toLowerCase().includes(searchHost.toLowerCase()) ||
    `${host.first_name} ${host.last_name}`.toLowerCase().includes(searchHost.toLowerCase())
  ) : [];

  return (
    <div className="admin-property-form">
      <h1>{isEdit ? t('admin.propertyForm.editTitle') : t('admin.propertyForm.createTitle')}</h1>
      <p className="subtitle">
        {isEdit ? t('admin.propertyForm.editSubtitle') : t('admin.propertyForm.createSubtitle')}
      </p>

      <form onSubmit={handleSubmit}>
        {/* Host Selection (only for new properties) */}
        {!isEdit && (
          <div className="form-section">
            <h2>{t('admin.propertyForm.selectHost')}</h2>
            <div className="form-group">
              <label>{t('admin.propertyForm.hostLabel')}</label>
              <input
                type="text"
                placeholder={t('admin.propertyForm.searchHostPlaceholder')}
                value={searchHost}
                onChange={(e) => setSearchHost(e.target.value)}
                className="search-input"
              />
              <select
                name="host_id"
                value={formData.host_id}
                onChange={handleChange}
                required
              >
                <option value="">{t('admin.propertyForm.selectHostOption')}</option>
                {filteredHosts.map(host => (
                  <option key={host.id} value={host.id}>
                    {host.first_name} {host.last_name} ({host.email})
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Basic Information */}
        <div className="form-section">
          <h2>{t('admin.propertyForm.basicInfo')}</h2>

          <div className="form-group">
            <label>{t('admin.propertyForm.title')}</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>{t('admin.propertyForm.description')}</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="5"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.propertyForm.propertyType')}</label>
              <select
                name="property_type"
                value={formData.property_type}
                onChange={handleChange}
                required
              >
                <option value="apartment">{t('admin.propertyForm.apartment')}</option>
                <option value="house">{t('admin.propertyForm.house')}</option>
                <option value="condo">{t('admin.propertyForm.condo')}</option>
                <option value="villa">{t('admin.propertyForm.villa')}</option>
                <option value="studio">{t('admin.propertyForm.studio')}</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Categoria do Imóvel</label>
              <select
                name="property_category"
                value={formData.property_category}
                onChange={handleChange}
              >
                <option value="single_unit">Unidade Individual</option>
                <option value="multi_unit">Multi-unidades (Prédio/Complexo)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Tipo de Listagem</label>
              <select
                name="listing_subtype"
                value={formData.listing_subtype}
                onChange={handleChange}
              >
                <option value="entire_place">Lugar Inteiro</option>
                <option value="private_room">Quarto Privado</option>
                <option value="shared_room">Quarto Compartilhado</option>
              </select>
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="form-section">
          <h2>{t('admin.propertyForm.location')}</h2>

          <div className="form-group">
            <label>{t('admin.propertyForm.address')}</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.propertyForm.city')}</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('admin.propertyForm.state')}</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.propertyForm.country')}</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>{t('admin.propertyForm.zipCode')}</label>
              <input
                type="text"
                name="zip_code"
                value={formData.zip_code}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* Property Details */}
        <div className="form-section">
          <h2>{t('admin.propertyForm.propertyDetails')}</h2>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.propertyForm.bedrooms')}</label>
              <input
                type="number"
                name="bedrooms"
                value={formData.bedrooms}
                onChange={handleChange}
                min="0"
                required
              />
            </div>

            <div className="form-group">
              <label>{t('admin.propertyForm.bathrooms')}</label>
              <input
                type="number"
                name="bathrooms"
                value={formData.bathrooms}
                onChange={handleChange}
                min="1"
                step="0.5"
                required
              />
            </div>

            <div className="form-group">
              <label>{t('admin.propertyForm.beds')}</label>
              <input
                type="number"
                name="beds"
                value={formData.beds}
                onChange={handleChange}
                min="1"
                required
              />
            </div>

            <div className="form-group">
              <label>Área (m²)</label>
              <input
                type="number"
                name="square_footage"
                value={formData.square_footage}
                onChange={handleChange}
                min="0"
                step="0.01"
                placeholder="Ex: 80.50"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.propertyForm.maxGuests')}</label>
              <input
                type="number"
                name="max_guests"
                value={formData.max_guests}
                onChange={handleChange}
                min="1"
                required
              />
            </div>

            <div className="form-group">
              <label>Hóspedes Inclusos no Preço Base</label>
              <input
                type="number"
                name="base_guests"
                value={formData.base_guests}
                onChange={handleChange}
                min="1"
                max={formData.max_guests}
                required
              />
              <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                Número de hóspedes já inclusos no preço da diária
              </small>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="form-section">
          <h2>{t('admin.propertyForm.pricing')}</h2>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.propertyForm.pricePerNight')}</label>
              <input
                type="number"
                name="price_per_night"
                value={formData.price_per_night}
                onChange={handleChange}
                min="1"
                step="0.01"
                required
              />
              <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                Preço base para até {formData.base_guests} hóspede(s)
              </small>
            </div>

            <div className="form-group">
              <label>Preço por Hóspede Adicional</label>
              <input
                type="number"
                name="extra_guest_price"
                value={formData.extra_guest_price}
                onChange={handleChange}
                min="0"
                step="0.01"
              />
              <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                Valor cobrado por cada hóspede além dos {formData.base_guests} inclusos
              </small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.propertyForm.cleaningFee')}</label>
              <input
                type="number"
                name="cleaning_fee"
                value={formData.cleaning_fee}
                onChange={handleChange}
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.propertyForm.serviceFee')}</label>
              <input
                type="number"
                name="service_fee"
                value={formData.service_fee}
                onChange={handleChange}
                min="0"
                step="0.01"
              />
            </div>
          </div>
        </div>

        {/* Rules & Policies */}
        <div className="form-section">
          <h2>{t('admin.propertyForm.rulesAndPolicies')}</h2>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.propertyForm.checkInTime')}</label>
              <input
                type="time"
                name="check_in_time"
                value={formData.check_in_time}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>{t('admin.propertyForm.checkOutTime')}</label>
              <input
                type="time"
                name="check_out_time"
                value={formData.check_out_time}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin.propertyForm.minimumNights')}</label>
              <input
                type="number"
                name="minimum_nights"
                value={formData.minimum_nights}
                onChange={handleChange}
                min="1"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.propertyForm.maximumNights')}</label>
              <input
                type="number"
                name="maximum_nights"
                value={formData.maximum_nights}
                onChange={handleChange}
                min="1"
              />
            </div>
          </div>

          <div className="form-group">
            <label>{t('admin.propertyForm.cancellationPolicy')}</label>
            <select
              name="cancellation_policy"
              value={formData.cancellation_policy}
              onChange={handleChange}
            >
              <option value="flexible">{t('admin.propertyForm.flexible')}</option>
              <option value="moderate">{t('admin.propertyForm.moderate')}</option>
              <option value="strict">{t('admin.propertyForm.strict')}</option>
            </select>
          </div>
        </div>

        {/* Status */}
        <div className="form-section">
          <h2>{t('admin.propertyForm.status')}</h2>

          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              <span>{t('admin.propertyForm.activeLabel')}</span>
            </label>
          </div>

          <div className="checkbox-group">
            <label>
              <input
                type="checkbox"
                name="is_featured"
                checked={formData.is_featured}
                onChange={handleChange}
              />
              <span>{t('admin.propertyForm.featuredLabel')}</span>
            </label>
          </div>
        </div>

        {/* Images */}
        <div className="form-section">
          <h2>{t('admin.propertyForm.propertyImages')}</h2>

          {isEdit && existingImages.length > 0 && (
            <div className="existing-images">
              <h3>{t('admin.propertyForm.currentImages')}</h3>
              <DraggableMediaList
                items={existingImages}
                onReorder={handleReorderImages}
                onRemove={handleRemoveExistingImage}
                onSetPrimary={handleSetPrimaryImage}
                type="image"
              />
            </div>
          )}

          <div className="form-group">
            <label>{t('admin.propertyForm.addImages')}</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              className="search-input"
            />
            <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
              {t('admin.propertyForm.selectMultiple')} {!isEdit || existingImages.length === 0 ? t('admin.propertyForm.firstPrimary') : ''}
            </small>
          </div>

          {selectedImages.length > 0 && (
            <div className="selected-images">
              <p><strong>{selectedImages.length}</strong> {t('admin.propertyForm.newImagesSelected')}</p>
              <DraggableMediaList
                items={selectedImages.map((file, idx) => ({
                  id: `new-${idx}`,
                  preview: URL.createObjectURL(file),
                  file: file
                }))}
                onReorder={(reordered) => setSelectedImages(reordered.map(item => item.file))}
                onRemove={handleRemoveImage}
                type="image"
              />
            </div>
          )}
        </div>

        {/* Videos */}
        <div className="form-section">
          <h2>{t('admin.propertyForm.propertyVideos')}</h2>

          {isEdit && existingVideos.length > 0 && (
            <div className="existing-videos">
              <h3>{t('admin.propertyForm.currentVideos')}</h3>
              <DraggableMediaList
                items={existingVideos}
                onReorder={handleReorderVideos}
                onRemove={handleRemoveExistingVideo}
                type="video"
              />
            </div>
          )}

          <div className="form-group">
            <label>{t('admin.propertyForm.addVideos')}</label>
            <input
              type="file"
              accept="video/*"
              multiple
              onChange={handleVideoSelect}
              className="search-input"
            />
            <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
              {t('admin.propertyForm.videoFormats')}
            </small>
          </div>

          {selectedVideos.length > 0 && (
            <div className="selected-videos">
              <p><strong>{selectedVideos.length}</strong> {t('admin.propertyForm.newVideosSelected')}</p>
              <DraggableMediaList
                items={selectedVideos.map((file, idx) => ({
                  id: `new-${idx}`,
                  preview: URL.createObjectURL(file),
                  file: file
                }))}
                onReorder={(reordered) => setSelectedVideos(reordered.map(item => item.file))}
                onRemove={handleRemoveVideo}
                type="video"
              />
            </div>
          )}
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/admin/properties')}
            className="btn-secondary"
            disabled={loading || uploadingImages || uploadingVideos}
          >
            {t('admin.propertyForm.cancel')}
          </button>
          <button type="submit" disabled={loading || uploadingImages || uploadingVideos} className="btn-primary">
            {uploadingVideos ? t('admin.propertyForm.uploadingVideos') : uploadingImages ? t('admin.propertyForm.uploadingImages') : loading ? t('admin.propertyForm.saving') : isEdit ? t('admin.propertyForm.updateProperty') : t('admin.propertyForm.createProperty')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminPropertyForm;
